import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, ArrowUp, ArrowDown, ImagePlus } from "lucide-react";
import api, { formatApiErrorDetail } from "./adminApi";
import { toast } from "sonner";

const fileUrl = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export default function AdminSpacePhotos() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/space-photos");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Ficheiro demasiado grande (máx. 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await api.post("/admin/space-photos", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((cur) => (cur ? [...cur, r.data].sort((a, b) => a.order - b.order) : [r.data]));
      toast.success("Foto adicionada.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const swapOrder = async (idx, dir) => {
    if (!items) return;
    const other = idx + dir;
    if (other < 0 || other >= items.length) return;
    const a = items[idx];
    const b = items[other];
    setBusyId(a.id);
    try {
      await api.patch(`/admin/space-photos/${a.id}`, { order: b.order });
      await api.patch(`/admin/space-photos/${b.id}`, { order: a.order });
      const next = [...items];
      next[idx] = { ...b, order: a.order };
      next[other] = { ...a, order: b.order };
      setItems(next.sort((x, y) => x.order - y.order));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Apagar esta foto do espaço?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/space-photos/${id}`);
      setItems((cur) => (cur ? cur.filter((p) => p.id !== id) : cur));
      toast.success("Foto apagada.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (items === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="admin-space-photos-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-white">Fotos do Espaço</h1>
          <p className="mt-1 text-sm text-white/50">
            Aparecem na Homepage e na página &quot;O Espaço&quot;. Ordem pode ser alterada com as setas.
          </p>
        </div>
        <label
          data-testid="space-photo-upload"
          className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition hover:bg-gold-light"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" strokeWidth={1.5} />
          )}
          {uploading ? "A enviar..." : "Adicionar foto"}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPickFile}
            disabled={uploading}
          />
        </label>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-16 text-center">
          <ImagePlus className="mx-auto h-12 w-12 text-white/30" strokeWidth={1.2} />
          <p className="mt-4 text-sm text-white/60">
            Ainda não há fotos do espaço. Enquanto isso, o site mostra covers das massagens como fallback.
          </p>
          <p className="mt-2 text-xs text-white/40">Adiciona a primeira foto acima.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p, i) => (
            <div
              key={p.id}
              data-testid={`space-photo-${p.id}`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]"
            >
              <div className="relative aspect-[4/3] bg-black/40">
                <img
                  src={fileUrl(p.url)}
                  alt={`Espaço ${i + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold">
                  #{i + 1}
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-white/10 bg-black/40 p-2">
                <div className="flex items-center gap-1">
                  <button
                    disabled={busyId === p.id || i === 0}
                    onClick={() => swapOrder(i, -1)}
                    title="Subir"
                    className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition hover:bg-white/10 hover:text-white disabled:opacity-30"
                  >
                    <ArrowUp className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                  <button
                    disabled={busyId === p.id || i === items.length - 1}
                    onClick={() => swapOrder(i, 1)}
                    title="Descer"
                    className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition hover:bg-white/10 hover:text-white disabled:opacity-30"
                  >
                    <ArrowDown className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
                <button
                  disabled={busyId === p.id}
                  data-testid={`space-photo-delete-${p.id}`}
                  onClick={() => remove(p.id)}
                  title="Apagar"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-red-300/80 transition hover:bg-red-500/10 hover:text-red-200 disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
